import { useEffect, useRef, useState } from 'react'
import { Helmet } from 'react-helmet'
import { useSearchParams } from 'react-router'

import { Spinner } from '@/components/ui/spinner'
import { confirmLargeFleetPacket, type PacketConfirmationStatus } from '@/models/largeFleetPacket'

const MESSAGES: Record<PacketConfirmationStatus, { title: string; body: string }> = {
  confirmed: {
    title: 'You’re all set',
    body: 'Thanks for confirming — a Cai broker will be in touch soon.',
  },
  expired: {
    title: 'This link has expired',
    body: 'Please submit your fleet info again and we’ll send a fresh confirmation link.',
  },
  not_found: {
    title: 'We couldn’t find that submission',
    body: 'This link may have already been used. Submit your fleet info again if you need to.',
  },
}

/** Lands here from the confirmation email — confirms the packet exactly once. */
export function LargeFleetConfirmPage() {
  const [searchParams] = useSearchParams()
  const token = searchParams.get('token')
  const [status, setStatus] = useState<PacketConfirmationStatus | 'loading'>('loading')
  const startedRef = useRef(false)

  useEffect(() => {
    if (!token || startedRef.current) {
      if (!token) setStatus('not_found')
      return
    }
    startedRef.current = true
    confirmLargeFleetPacket(token).then(setStatus)
  }, [token])

  const message = status === 'loading' ? undefined : MESSAGES[status]

  return (
    <div className="bg-ink-850 flex flex-1 flex-col items-center justify-center px-4 py-24">
      <Helmet>
        <title>Confirm submission</title>
      </Helmet>
      {message ? (
        <div className="mx-auto flex max-w-2xl flex-col items-start gap-4 text-left">
          <h1 className="h3 text-ink-50">{message.title}</h1>
          <p className="text-ink-200 text-lg leading-relaxed">{message.body}</p>
        </div>
      ) : (
        <div className="text-ink-200 flex items-center gap-2">
          <Spinner className="size-5" />
          <span>Confirming your submission…</span>
        </div>
      )}
    </div>
  )
}
